import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

const STORAGE_KEY = 'JDBH_LANG';

const dictionaries = {
  zh: {
    'app.title': '继电保护计算平台',
    'menu.home': '首页',
    'menu.calc': '保护计算',
    'menu.history': '计算历史',
    'menu.knowledge': '知识库',
    'menu.device_library': '设备库',
    'auth.login_register': '登录/注册',
    'loading.text': '正在加载系统...',
    'settings.general': '通用设置',
    'settings.password': '密码修改',
    'settings.calculation': '计算参数设置',
    'settings.about': '关于系统',
    'settings.ai': 'AI 模型设置',
    'logout': '退出登录',
    // 状态文案
    'status.online': '在线',
    'status.offline': '离线',
    // 认证与设置相关扩展文案
    'auth.title': '用户登录',
    'auth.login': '登录',
    'auth.register': '注册',
    'auth.email': '邮箱',
    'auth.password': '密码',
    'auth.confirm_password': '确认密码',
    'auth.or': '或',
    'auth.wechat_login': '微信登录',
    'wechat.title': '微信登录',
    'wechat.tip': '请使用微信扫描下方二维码登录',
    // 离线与队列相关
    'offline.banner': '离线模式：支持本地计算与离线队列，登录/注册不可用',
    'offline.retry_sync': '重试同步',
    'offline.view_queue': '查看队列',
    'offline.modal.title': '离线队列',
    'offline.modal.no_items': '暂无待同步任务',
    'offline.modal.clear': '清空队列',
    'offline.modal.remove': '移除',
    'offline.modal.cleared_success': '已清空离线队列',
    'offline.modal.removed_success': '已移除队列项',
    'offline.modal.name_label': '名称',
    'offline.modal.id_label': 'ID',
    'offline.type.save': '保存结果',
    'offline.type.update': '更新元数据',
    'offline.type.delete': '删除记录',
    'offline.history_unavailable': '离线模式下无法加载远端历史，您可继续在本地查看和导出。',
    'offline.history_cache_used': '离线模式使用本地缓存历史',
    'offline.history_cache_empty': '离线模式无本地历史缓存',
    'calc.save.offline_enqueued': '当前离线，已加入同步队列',
    'calc.save.failure_enqueued': '保存失败，已离线排队，同步后入库',
    'calc.save.success': '保存成功',
    'calc.save.exception_prefix': '保存异常: ',
    'calc.save.need_calculation': '请先完成计算再保存结果',
    'calc.save.need_login': '请先登录后再保存结果',
    'calc.save.need_name': '请填写名称',
    'calc.result.using_cache': '使用缓存的计算结果',
    'calc.result.done': '计算完成',
    'calc.result.error': '计算过程中发生错误',
    // HistoryPage 提示与过滤/排序
    'history.fetch.failed_prefix': '获取计算历史失败: ',
    'history.fetch.exception_prefix': '获取计算历史异常: ',
    'history.title': '计算历史',
    'history.not_logged_in': '未登录不能查看历史记录',
    'history.cache.cleared': '已清空本地缓存',
    'history.cache.clear_failed': '清空缓存失败',
    'history.cache.clear_button': '清空缓存',
    'history.delete.offline_enqueued_local': '当前离线，已加入同步队列并本地删除',
    'history.delete.failure_enqueued_local': '删除失败，已离线排队并本地删除',
    'history.delete.success': '删除成功',
    'history.delete.exception_prefix': '删除记录异常: ',
    'history.export.md.success': '报告导出成功',
    'history.export.failure_prefix': '导出失败: ',
    'history.copy_json.success': '记录JSON已复制到剪贴板',
    'history.copy_json.failure_prefix': '复制失败: ',
    'history.update.offline_enqueued_local': '当前离线，已加入同步队列并本地更新',
    'history.update.failure_enqueued_local': '更新失败，已离线排队并本地更新',
    'history.update.success': '已更新',
    'history.update.exception_prefix': '更新异常: ',
    'history.export.pdf.success': 'PDF 导出成功',
    'history.export.pdf.failure_prefix': 'PDF 导出失败: ',
    'history.print.opened': '已打开打印视图',
    'history.print.failure_prefix': '打印视图失败: ',
    'history.export.html.success': 'HTML 导出成功',
    'history.export.html.failure_prefix': 'HTML 导出失败: ',
    'history.export.word.success': 'Word 导出成功',
    'history.export.word.failure_prefix': 'Word 导出失败: ',
    'history.export.report.title': '历史记录计算报告',
    'share.copy.success': '分享链接已复制到剪贴板',
    'share.copy.info_fallback': '无法复制到剪贴板，已在新窗口打开',
    'share.generate.failure_prefix': '生成分享链接失败: ',
    'share.view.title': '分享查看',
    'share.link.invalid': '分享链接无效',
    'share.link.payload_invalid_or_expired': '链接载荷无效或已过期。',
    'share.signature.ok': '签名有效',
    'share.signature.invalid': '签名无效',
    'share.signature.missing': '签名缺失',
    'share.signature.failure': '签名校验失败，载荷可能被篡改',
    'share.token.none': '无Token',
    'share.token.used': '一次性Token已使用',
    'share.token.unused': '一次性Token未使用',
    'share.expired': '分享链接已过期',
    'share.export.container_missing': '未找到导出容器',
    'share.expires_until': '有效期至：',
    'share.export.report.title': '分享查看报告',
    'history.cache.timestamp_prefix': '缓存时间: '
    ,
    // AI 设置与曲线助手
    'ai.settings.title': 'AI 模型参数',
    'ai.settings.endpoint_url': '接口地址',
    'ai.settings.api_key': 'API Key',
    'ai.settings.model': '模型名称',
    'ai.settings.temperature': '生成温度',
    'ai.settings.max_tokens': '最大Tokens',
    'ai.settings.save_success': 'AI 模型参数已保存',
    'ai.ask_button': '问问 AI',
    'ai.panel.title': '曲线知识助手',
    'ai.panel.source.external': '来自外部模型',
    'ai.panel.source.local': '来自本地分析',
    'ai.panel.copy': '复制内容',
    'ai.panel.prompt_view': '查看提示词',
    'ai.panel.close': '关闭',
    // HistoryPage 过滤与排序控件
    'history.filter.protection_type': '保护类型',
    'history.search.by_name': '按名称搜索',
    'history.filter.project': '项目',
    'history.filter.line': '线路',
    'history.filter.tags': '标签',
    'history.sort.time_desc': '按时间(新→旧)',
    'history.sort.time_asc': '按时间(旧→新)',
    'history.sort.name_asc': '按名称(A→Z)',
    'history.sort.name_desc': '按名称(Z→A)',
    'history.sort.project_asc': '按项目(A→Z)',
    'history.sort.project_desc': '按项目(Z→A)',
    'history.sort.line_asc': '按线路(A→Z)',
    'history.sort.line_desc': '按线路(Z→A)',
    'history.filter.show_archived': '显示归档',
    'history.filter.reset': '重置筛选',
    // 通用保护类型名称（用于筛选项等）
    'protection.type.overcurrent': '过电流保护',
    'protection.type.distance': '距离保护',
    'protection.type.differential': '差动保护',
    'protection.type.directional': '方向保护',
    'protection.type.zero_sequence': '零序保护',
    'protection.type.longitudinal': '纵联差动'
    ,
    // CalculationPage 叠加控件
    'calc.overlay.add_from_history.placeholder': '从历史添加曲线',
    'calc.overlay.local_current': '当前计算（本地缓存）',
    'calc.overlay.history_empty_guidance': '历史为空或未登录：请登录或先打开历史页以生成缓存',
    'calc.overlay.clear': '清空叠加',
    'calc.overlay.legend.title': '图例与显隐',
    'calc.overlay.legend.show': '显示图例：',
    'calc.overlay.zoomX': '缩放X：',
    'calc.overlay.zoomY': '缩放Y：',
    'calc.overlay.panX': '平移X：',
    'calc.overlay.panY': '平移Y：',
    'calc.overlay.reset_view': '重置视图',
    'calc.overlay.show_all': '显示全部',
    'calc.overlay.reset_colors': '重置颜色',
    'calc.overlay.export_png': '导出叠加图 PNG',
    'calc.overlay.export_md': '导出校核结果 Markdown',
    'calc.overlay.md.title': '# 叠加曲线与选择性分析',
    'calc.overlay.md.count_prefix': '叠加曲线数：',
    'calc.overlay.md.list_title': '## 曲线列表',
    'calc.overlay.md.downstream': '下级(基准)',
    'calc.overlay.md.upstream': '上级',
    'calc.overlay.md.pickup_label': 'pickup',
    'calc.overlay.md.tms_label': 'TMS',
    'calc.overlay.md.curve_label': '曲线'
    ,
    // Overlay 排序与选中顺序文案
    'overlay.sort.by_selection_order_short': '选中顺序（第一个为下级）',
    'overlay.sort.by_rated_current_asc_short': '额定电流升序（下级→上级）',
    'overlay.md.sort_mode_prefix': '排序方式：',
    'overlay.md.selection_order_prefix': '选中顺序：',
    // ExportReport 排序文案
    'export.overlay.sort.by_panel_selection_prefix': '按面板选中顺序',
    'export.overlay.sort.by_current_view': '按当前视图顺序',
    // ExportReport 报告文案（Markdown/PDF）
    'export.report.md.title': '# 继电保护计算报告',
    'export.report.name_prefix': '- 名称: ',
    'export.report.type_prefix': '- 保护类型: ',
    'export.report.remark_prefix': '- 备注: ',
    'export.report.context.title': '## 上下文',
    'export.report.context.export_time_prefix': '- 导出时间: ',
    'export.report.context.project_prefix': '- 项目: ',
    'export.report.context.line_prefix': '- 线路: ',
    'export.report.context.tags_prefix': '- 标签: ',
    'export.report.context.version_stamp_prefix': '- 版本戳: ',
    'export.report.context.filename_prefix_resolved_prefix': '- 导出前缀（解析后）: ',
    'export.report.autotune.title': '## 自动整定变更日志',
    'export.report.autotune.action.apply': '应用建议',
    'export.report.autotune.action.rollback': '回滚至快照',
    'export.report.autotune.action.other': '事件',
    'export.report.autotune.event_prefix': '事件',
    'export.report.autotune.event_delimiter': '：',
    'export.report.autotune.goal_prefix': '- 目标: ',
    'export.report.autotune.applied_fields_prefix': '- 应用字段: ',
    'export.report.autotune.rationale_prefix': '- 理由: ',
    'export.report.warnings_prefix': '- 警告: ',
    'export.report.autotune.user_adjustments_prefix': '- 用户后续调整: ',
    'export.report.autotune.param_diffs_prefix': '- 参数差异: ',
    'export.report.device.title': '## 设备来源',
    'export.report.device.feeder_prefix': '- 馈线: ',
    'export.report.device.bus_voltage_prefix': '- 母线电压: ',
    'export.report.device.rated_current_prefix': '- 额定电流: ',
    'export.report.device.ct_ratio_prefix': '- CT变比: 1:',
    'export.report.device.source_prefix': '- 电源短路容量: ',
    'export.report.device.transformer_prefix': '- 变压器: ',
    'export.report.device.line_prefix': '- 线路: ',
    'export.report.device.ground_prefix': '- 接地阻抗: ',
    'export.report.device.feeder_id_prefix': '- 馈线ID: ',
    'export.report.device.ct_id_prefix': '- CT ID: ',
    'export.report.device.pt_id_prefix': '- PT ID: ',
    'export.report.device.relay_id_prefix': '- 继电器ID: ',
    'export.report.device_check.title': '## 设备校核',
    'export.report.device_check.blockers_title': '- 阻断项：请修正后再计算',
    'export.report.device_check.warnings_title': '- 提示项：建议对齐设备库参数',
    'export.report.input.title': '## 输入参数',
    'export.report.input.system_voltage_prefix': '- 系统电压: ',
    'export.report.input.rated_current_prefix': '- 额定电流: ',
    'export.report.input.ct_ratio_prefix': '- CT变比: 1:',
    'export.report.input.pt_ratio_prefix': '- PT变比: 1:',
    'export.report.input.fault_current_prefix': '- 故障电流: ',
    'export.report.input.transformer_capacity_prefix': '- 变压器容量: ',
    'export.report.input.curve_family_prefix': '- 反时限曲线族: ',
    'export.report.input.tms_prefix': '- 时间倍数 (TMS): ',
    'export.report.input.polarizing_angle_prefix': '- 极化角 (°): ',
    'export.report.input.k0_prefix': '- 零序比例系数 k0: ',
    'export.report.input.max_load_current_prefix': '- 最大负荷电流: ',
    'export.report.input.pickup_multiplier_prefix': '- 启动倍数: ',
    'export.report.input.instantaneous_multiplier_prefix': '- 瞬时倍数: ',
    'export.report.param_notes.title': '## 参数说明',
    'export.report.results.title': '## 计算结果',
    'export.report.engineering_warnings.title': '## 工程校核提示',
    'export.report.z_plane.title': '## 阻抗平面图 (R/X/θ)',
    'export.report.z_plane.axes_desc': '- 坐标轴: R(Ω)/X(Ω)，原点为测量基准；线路阻抗角以虚线标示。',
    'export.report.z_plane.zones_title': '- 分区定值: ',
    'export.report.z_plane.zone_margin_prefix': '区域裕度：',
    'export.report.chart.title': '## 图表',
    'export.report.chart.image_alt': '图表截图',
    'export.report.bar.title': '## 条形图',
    'export.report.bar.summary.title': '## 条形图数据摘要',
    'export.report.curve_screenshot.title': '## 反时限曲线截图',
    'export.report.curve_screenshot.image_alt': '曲线截图',
    'export.report.idir.image_alt': '差动特性图截图',
    'export.report.comments.title': '## 批注',
    'export.report.selectivity.title': '## 选择性校核',
    'export.report.selectivity.matrix_title': '多级选择性校核矩阵',
    'export.report.selectivity.context_prefix': '- 上下文: ',
    'export.report.selectivity.project_prefix': '项目 ',
    'export.report.selectivity.line_prefix': '线路 ',
    'export.report.selectivity.sort_prefix': '排序 ',
    'export.report.selectivity.min_margin_prefix': '- 最小裕度: ',
    'export.report.selectivity.summary.min_margin': '最小裕度',
    'export.report.selectivity.summary.overall_ok': '总体是否满足',
    'export.report.selectivity.table.headers.downstream': '下级',
    'export.report.selectivity.table.headers.upstream': '上级',
    'export.report.selectivity.table.headers.worst_margin': '最差裕度(s)',
    'export.report.selectivity.table.headers.worst_at_i': '最差点(A)',
    'export.report.selectivity.table.headers.pass': '通过',
    'export.report.selectivity.table.headers.min_margin': '最小裕度要求(s)',
    'export.report.selectivity.table.headers.recommend_tms': '建议TMS',
    'export.report.selectivity.table.headers.capped': '是否封顶',
    'export.report.selectivity.table.headers.note': '备注',
    'export.report.selectivity.no_data': '无可用的相邻链路校核数据。',
    'export.report.selectivity.overall_ok_prefix': '总体是否满足：',
    'export.common.yes': '是',
    'export.common.no': '否',
    'export.common.capped': '（受限）',
    'export.common.no_action': '不动作',
    'export.report.selectivity.adjustments.title': '### 顺序调整建议',
    'export.report.selectivity.note.min_margin_prefix': '最小裕度 ',
    'export.report.formulas.title': '## 计算公式',
    // Formulas by protection type
    'export.report.formulas.default.title': '保护计算公式',
    'export.report.formulas.default.placeholder': '请根据保护类型查看对应计算公式',
    'export.report.formulas.overcurrent.title': '过电流保护计算公式',
    'export.report.formulas.overcurrent.iop': '启动电流定值: Iop = Krel × Irated / nCT',
    'export.report.formulas.overcurrent.ksen': '灵敏系数: Ksen = If.min / Iop',
    'export.report.formulas.overcurrent.time': '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)',
    'export.report.formulas.overcurrent.notes': '其中: Krel=1.2~1.5，Irated为额定电流，nCT为CT变比；M=I/Iop；k、α为曲线常数（IEC 60255）',
    'export.report.formulas.distance.title': '距离保护计算公式',
    'export.report.formulas.distance.zm': '测量阻抗: Zm = U / I',
    'export.report.formulas.distance.zset1': '第I段定值: Zset.I = Krel × Zline',
    'export.report.formulas.distance.zset2': '第II段定值: Zset.II = Zline + Krel × Znext',
    'export.report.formulas.distance.zset3': '第III段定值: Zset.III = Krel × Zload.min / Kss.max',
    'export.report.formulas.distance.notes': '示意：Krel为可靠系数，Zline为线路阻抗；实际需校核弧光电阻、最小运行方式与区段配合',
    'export.report.formulas.differential.title': '差动保护计算公式',
    'export.report.formulas.differential.id': '差动电流: Id = |I1 + I2|',
    'export.report.formulas.differential.ir': '制动电流: Ir = (|I1| + |I2|) / 2',
    'export.report.formulas.differential.instant_trip': '差动速断: Id > Isd',
    'export.report.formulas.differential.ratio_restraint': '比率制动: Id > Icd + K × (Ir - Ibr)',
    'export.report.formulas.differential.harmonic_blocking': '二次谐波制动: I2/I1 > Kb',
    'export.report.formulas.differential.notes': '其中: Isd为速断定值, Icd为差动定值, K为制动系数',
    'export.report.formulas.directional.title': '方向保护计算公式',
    'export.report.formulas.directional.vsec': '二次电压: Vsec = U / nPT',
    'export.report.formulas.directional.isec': '二次电流: Isec = If / nCT',
    'export.report.formulas.directional.p_criterion': '相量有功分量: P = Re{V × I*} ≈ Vsec × Isec × cos(φ − θp)',
    'export.report.formulas.directional.direction_criterion': '方向判据: sign(Re{V × I*})（示意）',
    'export.report.formulas.directional.iop': '启动电流: Iop = 1.1 × Irated / nCT',
    'export.report.formulas.directional.multiple': '倍数: M = Isec / Iop',
    'export.report.formulas.directional.time': '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)',
    'export.report.formulas.zero_sequence.title': '零序保护计算公式',
    'export.report.formulas.zero_sequence.i0': '二次零序电流: I0 = k0 × If / nCT',
    'export.report.formulas.zero_sequence.iop0': '启动电流: Iop0 = 0.4 × Irated / nCT',
    'export.report.formulas.zero_sequence.multiple': '倍数: M = I0 / Iop0',
    'export.report.formulas.zero_sequence.time': '动作时间: t = TMS × k / (M^α - 1) (IEC 60255)',
    'export.report.formulas.zero_sequence.notes': '其中: k0 为近似换算系数，用于从一次零序电流估算二次值；实际取值需结合系统接地方式与装置系数校核',
    'export.report.formulas.longitudinal.title': '纵联差动保护计算公式',
    'export.report.formulas.longitudinal.isec': '二次电流: Isec = If / nCT',
    'export.report.formulas.longitudinal.id': '差动电流: Id = |I本端 + I对端|（单侧供电近似 Id ≈ Isec）',
    'export.report.formulas.longitudinal.iop': '启动电流: Iop = 1.2 × Irated / nCT',
    'export.report.formulas.longitudinal.time': '动作时间: t = tdef（快速动作，典型 0.05s）',
    // Common formula texts
  'export.report.formulas.common.if_min': '最小故障电流（二次）: If.min = min(If) / nCT',
  'export.report.formulas.common.if_min_explain': '最小一次故障电流 / CT变比',
    // Id–Ir chart descriptions
    'export.report.idir.axes_desc': '- 横轴为制动电流 Ir(A)，纵轴为差动电流 Id(A)；分段斜率与速断元件按曲线标注。',
    'export.report.idir.segments_title': '- 特性分段: ',
    'export.report.idir.table.headers.segment': '段序',
    'export.report.idir.table.headers.slope': '斜率K',
    'export.report.idir.table.headers.threshold': '阈值 Ir(A)',
    'export.report.idir.table.headers.note': '说明',
    'export.report.header.name_label': '名称',
    'export.report.header.type_label': '类型',
    'export.report.header.time_label': '时间',
    'export.report.header.title': '继电保护计算报告',
    'export.report.header.unnamed': '未命名',
    'export.report.axes.i_t_label': 'I-t',
    'export.report.axes.z_label': 'Z',
    'export.report.axes.idir_label': 'Id-Ir',
    // Onboarding 引导
    'onboarding.title': '首次使用引导',
    'onboarding.desc': '按如下步骤完成“模板→计算→保存→叠加→导出”的闭环。',
    'onboarding.step.template': '选择参数模板',
    'onboarding.step.calculate': '执行计算并查看结果',
    'onboarding.step.save': '保存为历史记录',
    'onboarding.step.overlay': '叠加曲线进行对比/校核',
    'onboarding.step.export': '导出视图或报告',
    'onboarding.cta.start': '立即开始',
    'onboarding.cta.history': '查看历史',
    // Overlay 交互增强
    'calc.overlay.tooltips': '悬浮提示：',
    'calc.overlay.box_zoom': '框选缩放：',
    'calc.overlay.colorblind_mode': '色盲友好色板：',
    'calc.overlay.legend.rename': '重命名',
    'calc.overlay.legend.move_up': '上移',
    'calc.overlay.legend.move_down': '下移',
    'calc.overlay.legend.ref_line': '参考线'
  },
  en: {
    'app.title': 'Relay Protection Calculator',
    'menu.home': 'Home',
    'menu.calc': 'Calculation',
    'menu.history': 'History',
    'menu.knowledge': 'Knowledge',
    'menu.device_library': 'Device Library',
    'auth.login_register': 'Login / Register',
    'loading.text': 'Loading system...',
    'settings.general': 'General Settings',
    'settings.password': 'Change Password',
    'settings.calculation': 'Calculation Settings',
    'settings.about': 'About',
    'settings.ai': 'AI Model Settings',
    'logout': 'Logout',
    // Status labels
    'status.online': 'Online',
    'status.offline': 'Offline',
    // Auth & settings labels
    'auth.title': 'User Login',
    'auth.login': 'Login',
    'auth.register': 'Register',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.confirm_password': 'Confirm Password',
    'auth.or': 'OR',
    'auth.wechat_login': 'WeChat Login',
    'wechat.title': 'WeChat Login',
    'wechat.tip': 'Please scan the QR code below to login',
    // Offline & queue labels
    'offline.banner': 'Offline mode: Local calculations and offline queue; auth disabled',
    'offline.retry_sync': 'Retry Sync',
    'offline.view_queue': 'View Queue',
    'offline.modal.title': 'Offline Queue',
    'offline.modal.no_items': 'No pending tasks',
    'offline.modal.clear': 'Clear Queue',
    'offline.modal.remove': 'Remove',
    'offline.modal.cleared_success': 'Offline queue cleared',
    'offline.modal.removed_success': 'Queue item removed',
    'offline.modal.name_label': 'Name',
    'offline.modal.id_label': 'ID',
    'offline.type.save': 'Save Result',
    'offline.type.update': 'Update Metadata',
    'offline.type.delete': 'Delete Record',
    'offline.history_unavailable': 'Offline: cannot load remote history; local view/export still available.',
    'offline.history_cache_used': 'Using local cached history in offline mode',
    'offline.history_cache_empty': 'No local history cache available in offline mode',
    'calc.save.offline_enqueued': 'Offline: task enqueued for sync',
    'calc.save.failure_enqueued': 'Save failed; enqueued for sync on reconnect',
    'calc.save.success': 'Saved successfully',
    'calc.save.exception_prefix': 'Save error: ',
    'calc.save.need_calculation': 'Please compute first before saving',
    'calc.save.need_login': 'Please login before saving',
    'calc.save.need_name': 'Please fill in a name',
    'calc.result.using_cache': 'Using cached calculation result',
    'calc.result.done': 'Calculation completed',
    'calc.result.error': 'Error occurred during calculation',
    // HistoryPage messages & filters/sort
    'history.fetch.failed_prefix': 'Failed to fetch history: ',
    'history.fetch.exception_prefix': 'History fetch error: ',
    'history.title': 'Calculation History',
    'history.not_logged_in': 'Not logged in; cannot view history',
    'history.cache.cleared': 'Local cache cleared',
    'history.cache.clear_failed': 'Clear cache failed',
    'history.cache.clear_button': 'Clear Cache',
    'history.delete.offline_enqueued_local': 'Offline: enqueued and deleted locally',
    'history.delete.failure_enqueued_local': 'Delete failed; enqueued and deleted locally',
    'history.delete.success': 'Deleted successfully',
    'history.delete.exception_prefix': 'Delete error: ',
    'history.export.md.success': 'Report exported',
    'history.export.failure_prefix': 'Export failed: ',
    'history.copy_json.success': 'Record JSON copied to clipboard',
    'history.copy_json.failure_prefix': 'Copy failed: ',
    'history.update.offline_enqueued_local': 'Offline: enqueued and updated locally',
    'history.update.failure_enqueued_local': 'Update failed; enqueued and updated locally',
    'history.update.success': 'Updated',
    'history.update.exception_prefix': 'Update error: ',
    'history.export.pdf.success': 'PDF exported',
    'history.export.pdf.failure_prefix': 'PDF export failed: ',
    'history.print.opened': 'Print view opened',
    'history.print.failure_prefix': 'Print view failed: ',
    'history.export.html.success': 'HTML exported',
    'history.export.html.failure_prefix': 'HTML export failed: ',
    'history.export.word.success': 'Word exported',
    'history.export.word.failure_prefix': 'Word export failed: ',
    'history.export.report.title': 'Calculation Report (History)',
    'share.copy.success': 'Share link copied to clipboard',
    'share.copy.info_fallback': 'Could not copy; opened in a new window',
    'share.generate.failure_prefix': 'Share link generation failed: ',
    'share.view.title': 'Share View',
    'share.link.invalid': 'Invalid share link',
    'share.link.payload_invalid_or_expired': 'Payload invalid or expired.',
    'share.signature.ok': 'Signature valid',
    'share.signature.invalid': 'Signature invalid',
    'share.signature.missing': 'Signature missing',
    'share.signature.failure': 'Signature verification failed; payload may be tampered',
    'share.token.none': 'No token',
    'share.token.used': 'One-time token used',
    'share.token.unused': 'One-time token unused',
    'share.expired': 'Share link expired',
    'share.export.container_missing': 'Export container not found',
    'share.expires_until': 'Valid until: ',
    'share.export.report.title': 'Share View Report',
    'history.cache.timestamp_prefix': 'Cache time: '
    ,
    // AI settings and curve assistant
    'ai.settings.title': 'AI Model Parameters',
    'ai.settings.endpoint_url': 'Endpoint URL',
    'ai.settings.api_key': 'API Key',
    'ai.settings.model': 'Model Name',
    'ai.settings.temperature': 'Temperature',
    'ai.settings.max_tokens': 'Max Tokens',
    'ai.settings.save_success': 'AI model settings saved',
    'ai.ask_button': 'Ask AI',
    'ai.panel.title': 'Curve Knowledge Assistant',
    'ai.panel.source.external': 'From external model',
    'ai.panel.source.local': 'From local analysis',
    'ai.panel.copy': 'Copy content',
    'ai.panel.prompt_view': 'View prompt',
    'ai.panel.close': 'Close',
    // HistoryPage filters & sort controls
    'history.filter.protection_type': 'Protection Type',
    'history.search.by_name': 'Search by name',
    'history.filter.project': 'Project',
    'history.filter.line': 'Line',
    'history.filter.tags': 'Tags',
    'history.sort.time_desc': 'By time (new → old)',
    'history.sort.time_asc': 'By time (old → new)',
    'history.sort.name_asc': 'By name (A → Z)',
    'history.sort.name_desc': 'By name (Z → A)',
    'history.sort.project_asc': 'By project (A → Z)',
    'history.sort.project_desc': 'By project (Z → A)',
    'history.sort.line_asc': 'By line (A → Z)',
    'history.sort.line_desc': 'By line (Z → A)',
    'history.filter.show_archived': 'Show archived',
    'history.filter.reset': 'Reset filters',
    // Generic protection type names (for filter options etc.)
    'protection.type.overcurrent': 'Overcurrent Protection',
    'protection.type.distance': 'Distance Protection',
    'protection.type.differential': 'Differential Protection',
    'protection.type.directional': 'Directional Protection',
    'protection.type.zero_sequence': 'Zero Sequence Protection',
    'protection.type.longitudinal': 'Longitudinal Differential'
    ,
    // CalculationPage overlay controls
    'calc.overlay.add_from_history.placeholder': 'Add curve from history',
    'calc.overlay.local_current': 'Current calculation (local cache)',
    'calc.overlay.history_empty_guidance': 'History empty or not logged in: login or open History to cache first',
    'calc.overlay.clear': 'Clear overlay',
    'calc.overlay.legend.title': 'Legend & Visibility',
    'calc.overlay.legend.show': 'Show legend:',
    'calc.overlay.zoomX': 'Zoom X:',
    'calc.overlay.zoomY': 'Zoom Y:',
    'calc.overlay.panX': 'Pan X:',
    'calc.overlay.panY': 'Pan Y:',
    'calc.overlay.reset_view': 'Reset View',
    'calc.overlay.show_all': 'Show All',
    'calc.overlay.reset_colors': 'Reset Colors',
    'calc.overlay.export_png': 'Export overlay PNG',
    'calc.overlay.export_md': 'Export analysis Markdown',
    'calc.overlay.md.title': '# Overlay curves & selectivity analysis',
    'calc.overlay.md.count_prefix': 'Overlay count: ',
    'calc.overlay.md.list_title': '## Curve List',
    'calc.overlay.md.downstream': 'Downstream (baseline)',
    'calc.overlay.md.upstream': 'Upstream',
    'calc.overlay.md.pickup_label': 'pickup',
    'calc.overlay.md.tms_label': 'TMS',
    'calc.overlay.md.curve_label': 'curve'
    ,
    // Overlay sort & selection labels
    'overlay.sort.by_selection_order_short': 'By selection order (first is downstream)',
    'overlay.sort.by_rated_current_asc_short': 'Rated current ascending (downstream → upstream)',
    'overlay.md.sort_mode_prefix': 'Sort mode: ',
    'overlay.md.selection_order_prefix': 'Selection order: ',
    // ExportReport sort labels
    'export.overlay.sort.by_panel_selection_prefix': 'By panel selection order',
    'export.overlay.sort.by_current_view': 'By current view order',
    // ExportReport report labels (Markdown/PDF)
    'export.report.md.title': '# Relay Protection Calculation Report',
    'export.report.name_prefix': '- Name: ',
    'export.report.type_prefix': '- Protection Type: ',
    'export.report.remark_prefix': '- Remark: ',
    'export.report.context.title': '## Context',
    'export.report.context.export_time_prefix': '- Exported at: ',
    'export.report.context.project_prefix': '- Project: ',
    'export.report.context.line_prefix': '- Line: ',
    'export.report.context.tags_prefix': '- Tags: ',
    'export.report.context.version_stamp_prefix': '- Version stamp: ',
    'export.report.context.filename_prefix_resolved_prefix': '- Filename prefix (resolved): ',
    'export.report.autotune.title': '## Auto-tuning Change Log',
    'export.report.autotune.action.apply': 'Apply suggestion',
    'export.report.autotune.action.rollback': 'Rollback to snapshot',
    'export.report.autotune.action.other': 'Event',
    'export.report.autotune.event_prefix': 'Event',
    'export.report.autotune.event_delimiter': ':',
    'export.report.autotune.goal_prefix': '- Goal: ',
    'export.report.autotune.applied_fields_prefix': '- Applied fields: ',
    'export.report.autotune.rationale_prefix': '- Rationale: ',
    'export.report.warnings_prefix': '- Warnings: ',
    'export.report.autotune.user_adjustments_prefix': '- User adjustments: ',
    'export.report.autotune.param_diffs_prefix': '- Parameter diffs: ',
    'export.report.device.title': '## Device Source',
    'export.report.device.feeder_prefix': '- Feeder: ',
    'export.report.device.bus_voltage_prefix': '- Bus voltage: ',
    'export.report.device.rated_current_prefix': '- Rated current: ',
    'export.report.device.ct_ratio_prefix': '- CT ratio: 1:',
    'export.report.device.source_prefix': '- Source short-circuit capacity: ',
    'export.report.device.transformer_prefix': '- Transformer: ',
    'export.report.device.line_prefix': '- Line: ',
    'export.report.device.ground_prefix': '- Ground impedance: ',
    'export.report.device.feeder_id_prefix': '- Feeder ID: ',
    'export.report.device.ct_id_prefix': '- CT ID: ',
    'export.report.device.pt_id_prefix': '- PT ID: ',
    'export.report.device.relay_id_prefix': '- Relay ID: ',
    'export.report.device_check.title': '## Device Check',
    'export.report.device_check.blockers_title': '- Blocking items: please fix and recalculate',
    'export.report.device_check.warnings_title': '- Hints: please align with device library parameters',
    'export.report.input.title': '## Input Parameters',
    'export.report.input.system_voltage_prefix': '- System voltage: ',
    'export.report.input.rated_current_prefix': '- Rated current: ',
    'export.report.input.ct_ratio_prefix': '- CT ratio: 1:',
    'export.report.input.pt_ratio_prefix': '- PT ratio: 1:',
    'export.report.input.fault_current_prefix': '- Fault current: ',
    'export.report.input.transformer_capacity_prefix': '- Transformer capacity: ',
    'export.report.input.curve_family_prefix': '- Inverse-time curve family: ',
    'export.report.input.tms_prefix': '- Time multiplier (TMS): ',
    'export.report.input.polarizing_angle_prefix': '- Polarizing angle (°): ',
    'export.report.input.k0_prefix': '- Zero-sequence coefficient k0: ',
    'export.report.param_notes.title': '## Parameter Notes',
    'export.report.results.title': '## Calculation Results',
    'export.report.engineering_warnings.title': '## Engineering Checks',
    'export.report.z_plane.title': '## Impedance Plane (R/X/θ)',
    'export.report.z_plane.axes_desc': '- Axes: R(Ω)/X(Ω), origin as measurement baseline; line impedance angle dashed.',
    'export.report.z_plane.zones_title': '- Zone settings: ',
    'export.report.chart.title': '## Chart',
    'export.report.chart.image_alt': 'Chart screenshot',
    'export.report.bar.title': '## Bar Chart',
    'export.report.bar.summary.title': '## Bar Chart Summary',
    'export.report.curve_screenshot.title': '## Inverse-time Curve Screenshot',
    'export.report.curve_screenshot.image_alt': 'Curve screenshot',
    'export.report.comments.title': '## Comments',
    'export.report.selectivity.title': '## Selectivity Check',
    'export.report.selectivity.matrix_title': 'Multi-level Selectivity Matrix',
    'export.report.selectivity.context_prefix': '- Context: ',
    'export.report.selectivity.project_prefix': 'Project ',
    'export.report.selectivity.line_prefix': 'Line ',
    'export.report.selectivity.sort_prefix': 'Sort ',
    'export.report.selectivity.min_margin_prefix': '- Min margin: ',
    'export.report.selectivity.table.headers.downstream': 'Downstream',
    'export.report.selectivity.table.headers.upstream': 'Upstream',
    'export.report.selectivity.table.headers.worst_margin': 'Worst margin (s)',
    'export.report.selectivity.table.headers.worst_at_i': 'Worst point (A)',
    'export.report.selectivity.table.headers.pass': 'Pass',
    'export.report.selectivity.table.headers.min_margin': 'Min margin (s)',
    'export.report.selectivity.table.headers.recommend_tms': 'Recommended TMS',
    'export.report.selectivity.table.headers.capped': 'Capped',
    'export.report.selectivity.table.headers.note': 'Note',
    'export.report.selectivity.no_data': 'No adjacent link selectivity data available.',
    'export.report.selectivity.overall_ok_prefix': 'Overall Pass: ',
    'export.report.selectivity.summary.min_margin': 'Min margin',
    'export.report.selectivity.summary.overall_ok': 'Overall pass',
    'export.common.yes': 'Yes',
    'export.common.no': 'No',
    'export.common.capped': '(capped)',
    'export.common.no_action': 'No action',
    'export.report.selectivity.adjustments.title': '### Order Adjustment Suggestions',
    'export.report.selectivity.note.min_margin_prefix': 'Min margin ',
    'export.report.formulas.title': '## Formulas',
    // Formulas by protection type
    'export.report.formulas.default.title': 'Protection Formulas',
    'export.report.formulas.default.placeholder': 'Please refer to formulas by protection type',
    'export.report.formulas.overcurrent.title': 'Overcurrent Protection Formulas',
    'export.report.formulas.overcurrent.iop': 'Pickup current: Iop = Krel × Irated / nCT',
    'export.report.formulas.overcurrent.ksen': 'Sensitivity: Ksen = If.min / Iop',
    'export.report.formulas.overcurrent.time': 'Operating time: t = TMS × k / (M^α - 1) (IEC 60255)',
    'export.report.formulas.overcurrent.notes': 'Notes: Krel=1.2~1.5, Irated is rated current, nCT is CT ratio; M=I/Iop; k, α are curve constants (IEC 60255)',
    'export.report.formulas.distance.title': 'Distance Protection Formulas',
    'export.report.formulas.distance.zm': 'Measured impedance: Zm = U / I',
    'export.report.formulas.distance.zset1': 'Zone I setting: Zset.I = Krel × Zline',
    'export.report.formulas.distance.zset2': 'Zone II setting: Zset.II = Zline + Krel × Znext',
    'export.report.formulas.distance.zset3': 'Zone III setting: Zset.III = Krel × Zload.min / Kss.max',
    'export.report.formulas.distance.notes': 'Indicative: Krel is reliability coefficient, Zline is line impedance; check arc resistance, minimum operating mode, and zone coordination',
    'export.report.formulas.differential.title': 'Differential Protection Formulas',
    'export.report.formulas.differential.id': 'Differential current: Id = |I1 + I2|',
    'export.report.formulas.differential.ir': 'Restraining current: Ir = (|I1| + |I2|) / 2',
    'export.report.formulas.differential.instant_trip': 'Instant trip: Id > Isd',
    'export.report.formulas.differential.ratio_restraint': 'Ratio restraint: Id > Icd + K × (Ir - Ibr)',
    'export.report.formulas.differential.harmonic_blocking': '2nd harmonic blocking: I2/I1 > Kb',
    'export.report.formulas.differential.notes': 'Notes: Isd is instant setting, Icd is differential setting, K is restraint coefficient',
    'export.report.formulas.directional.title': 'Directional Protection Formulas',
    'export.report.formulas.directional.vsec': 'Secondary voltage: Vsec = U / nPT',
    'export.report.formulas.directional.isec': 'Secondary current: Isec = If / nCT',
    'export.report.formulas.directional.p_criterion': 'Phasor active component: P = Re{V × I*} ≈ Vsec × Isec × cos(φ − θp)',
    'export.report.formulas.directional.direction_criterion': 'Direction criterion: sign(Re{V × I*}) (indicative)',
    'export.report.formulas.directional.iop': 'Pickup: Iop = 1.1 × Irated / nCT',
    'export.report.formulas.directional.multiple': 'Multiple: M = Isec / Iop',
    'export.report.formulas.directional.time': 'Operating time: t = TMS × k / (M^α - 1) (IEC 60255)',
    'export.report.formulas.zero_sequence.title': 'Zero-sequence Protection Formulas',
    'export.report.formulas.zero_sequence.i0': 'Secondary zero-sequence current: I0 = k0 × If / nCT',
    'export.report.formulas.zero_sequence.iop0': 'Pickup: Iop0 = 0.4 × Irated / nCT',
    'export.report.formulas.zero_sequence.multiple': 'Multiple: M = I0 / Iop0',
    'export.report.formulas.zero_sequence.time': 'Operating time: t = TMS × k / (M^α - 1) (IEC 60255)',
    'export.report.formulas.zero_sequence.notes': 'Notes: k0 is an approximate coefficient converting primary zero-sequence current to secondary; tune per grounding scheme and relay factors',
    'export.report.formulas.longitudinal.title': 'Pilot Differential Protection Formulas',
    'export.report.formulas.longitudinal.isec': 'Secondary current: Isec = If / nCT',
    'export.report.formulas.longitudinal.id': 'Differential current: Id = |Ilocal + Iremote| (single-source approx. Id ≈ Isec)',
    'export.report.formulas.longitudinal.iop': 'Pickup: Iop = 1.2 × Irated / nCT',
    'export.report.formulas.longitudinal.time': 'Operating time: t = tdef (fast action, typical 0.05s)',
    // Common formula texts
  'export.report.formulas.common.if_min': 'Minimum fault current (secondary): If.min = min(If) / nCT',
  'export.report.formulas.common.if_min_explain': 'Minimum primary fault current / CT ratio',
    // Id–Ir chart descriptions
    'export.report.idir.axes_desc': '- X-axis: restraining current Ir(A), Y-axis: differential current Id(A); segment slopes and instant elements are labeled on the curve.',
    'export.report.idir.segments_title': '- Segments: ',
    'export.report.idir.table.headers.segment': 'Segment',
    'export.report.idir.table.headers.slope': 'Slope K',
    'export.report.idir.table.headers.threshold': 'Threshold Ir(A)',
    'export.report.idir.table.headers.note': 'Note',
    'export.report.header.name_label': 'Name',
    'export.report.header.type_label': 'Type',
    'export.report.header.time_label': 'Time',
    'export.report.header.title': 'Relay Protection Calculation Report',
    'export.report.header.unnamed': 'unnamed',
    'export.report.axes.i_t_label': 'I-t',
    'export.report.axes.z_label': 'Z',
    'export.report.axes.idir_label': 'Id-Ir',
    'export.report.z_plane.zone_margin_prefix': 'Zone margin: ',
    'export.report.idir.image_alt': 'Id–Ir chart screenshot',
    // Onboarding guide
    'onboarding.title': 'Getting Started',
    'onboarding.desc': 'Follow steps to complete Template → Calculate → Save → Overlay → Export.',
    'onboarding.step.template': 'Choose a parameter template',
    'onboarding.step.calculate': 'Run calculation and review results',
    'onboarding.step.save': 'Save as a record',
    'onboarding.step.overlay': 'Overlay curves for comparison/selectivity',
    'onboarding.step.export': 'Export view or report',
    'onboarding.cta.start': 'Start Now',
    'onboarding.cta.history': 'Open History',
    // Overlay interactions
    'calc.overlay.tooltips': 'Hover tooltips:',
    'calc.overlay.box_zoom': 'Box zoom:',
    'calc.overlay.colorblind_mode': 'Colorblind-friendly palette:',
    'calc.overlay.legend.rename': 'Rename',
    'calc.overlay.legend.move_up': 'Move Up',
    'calc.overlay.legend.move_down': 'Move Down',
    'calc.overlay.legend.ref_line': 'Reference line'
  },
};

const I18nContext = createContext({ t: (k) => k, lang: 'zh', setLang: () => {} });

export const I18nProvider = ({ children, initialLang }) => {
  const [lang, setLang] = useState(initialLang || 'zh');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setLang(saved);
    } catch (_) {}
  }, []);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (_) {}
  }, [lang]);

  const dict = dictionaries[lang] || dictionaries.zh;
  const t = useMemo(() => (key) => dict[key] || key, [dict]);

  const value = useMemo(() => ({ t, lang, setLang }), [t, lang]);

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);